import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { NotificationInfo } from '@/common/components/Notifications/interfaces/notification'

interface NotificationsState {
  notificationsList: NotificationInfo[]
  idCounter: number
}

/**
 * Tope de avisos visibles a la vez. Cada consulta que falla dispara su propio toast
 * (hay más de 500 puntos en la app que los emiten), así que sin tope una pantalla que
 * carga varias consultas tapa el contenido con una pila de avisos.
 */
const MAXIMO_VISIBLE = 3

const initialState: NotificationsState = {
  notificationsList: [],
  idCounter: 0,
}

/**
 * Genera un ID único para cada notificación
 * Combina timestamp con un contador incremental para evitar colisiones
 * incluso cuando múltiples notificaciones se crean en el mismo milisegundo
 */
const generateUniqueId = (counter: number): number => {
  return Date.now() * 10000 + counter
}

const esElMismoAviso = (
  notificacion: NotificationInfo,
  otra: Omit<NotificationInfo, 'id'>,
): boolean =>
  notificacion.title === otra.title &&
  notificacion.message === otra.message &&
  notificacion.style === otra.style

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification(
      state,
      action: PayloadAction<{ value: Omit<NotificationInfo, 'id'> }>,
    ) {
      const { message, style, title } = action.payload.value

      // Incrementar el contador para garantizar IDs únicos
      state.idCounter += 1

      const repetido = state.notificationsList.find((notificacion) =>
        esElMismoAviso(notificacion, action.payload.value),
      )

      if (repetido) {
        // Se mantiene el id -y con él la posición y el componente montado-, y se reinicia
        // el temporizador con reiniciadoEn para que el aviso no desaparezca mientras el
        // problema sigue ocurriendo.
        repetido.repeticiones = (repetido.repeticiones ?? 1) + 1
        repetido.reiniciadoEn = generateUniqueId(state.idCounter)

        return
      }

      state.notificationsList.unshift({
        id: generateUniqueId(state.idCounter),
        message,
        style,
        title,
        repeticiones: 1,
      })

      // unshift deja el más nuevo al inicio, así que el sobrante a descartar es el final
      if (state.notificationsList.length > MAXIMO_VISIBLE) {
        state.notificationsList.length = MAXIMO_VISIBLE
      }
    },
    removeNotification(state, action: PayloadAction<{ value: number }>) {
      const listUpdated = state.notificationsList.filter(
        (notification) => notification.id !== action.payload.value,
      )

      state.notificationsList = listUpdated
    },
  },
})

export const { addNotification, removeNotification } =
  notificationsSlice.actions

const NotificationsReducer = notificationsSlice.reducer

export default NotificationsReducer
